import React, { useState, useEffect } from 'react';
import {
  BuildingStorefrontIcon,
  LinkIcon,
  MapPinIcon,
  PhoneIcon,
  ReceiptPercentIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import storeApi from '../../../../services/storeApi';
import StoreUpsertModal from './components/StoreUpsertModal';

const Stores = ({ companyDetail, setNotf }) => {
  const [loading, setLoading] = useState({ storeListLoading: true });
  const [dialogs, setDialogs] = useState({ upsert: false });
  const [storeList, setStoreList] = useState([]);
  const [selectedStore, setSelectedStore] = useState({});

  const getStoreList = async () => {
    try {
      setLoading((prev) => ({ ...prev, storeListLoading: true }));
      const query = `companyid=${companyDetail.id}`;
      const list = await storeApi.list(query);
      setStoreList(list.data?.data || []);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
      setStoreList([]);
    } finally {
      setLoading((prev) => ({ ...prev, storeListLoading: false }));
    }
  };

  const checkUpdate = (val, message) => {
    if (val) {
      setNotf((prev) => ({
        ...prev,
        message: 'Store Updated Successfully',
        display: true,
        type: 'success',
      }));
    } else {
      setNotf((prev) => ({
        ...prev,
        message,
        display: true,
        type: 'error',
      }));
    }
    setSelectedStore({});
    getStoreList();
  };

  const onClickOpenStoreUpsertModal = (store) => {
    setSelectedStore({ ...store });
    setDialogs({ ...dialogs, upsert: true });
  };

  useEffect(() => {
    getStoreList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderAddress = (store) => {
    const parts = [store.address, store.apartment, store.city]
      .filter((x) => x && String(x).trim())
      .join(', ');
    const line2 = [store.state, store.postalcode]
      .filter((x) => x && String(x).trim())
      .join(', ');
    return (
      <div className="space-y-0.5">
        {parts && <p>{parts}</p>}
        {line2 && <p>{line2}</p>}
      </div>
    );
  };

  return (
    <section className="space-y-5">
      {loading.storeListLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex items-center gap-3">
                <SkeletonBlock className="h-10 w-10 !rounded-md" />
                <SkeletonBlock className="h-5 w-40" />
              </div>
              <div className="mt-4 space-y-2">
                <SkeletonBlock className="h-4 w-3/4" />
                <SkeletonBlock className="h-4 w-2/3" />
                <SkeletonBlock className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : storeList.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-600 dark:bg-slate-800">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
            <BuildingStorefrontIcon className="h-7 w-7" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              No stores yet.
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Stores linked to this company will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {storeList.map((store) => {
            const orderUrl = `${process.env.REACT_APP_ORDER_URL || ''}/${store.slug || ''}`;
            return (
              <article
                key={store.id}
                className="group overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                      <BuildingStorefrontIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                        {store.name || '—'}
                      </h3>
                      {store.slug && (
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          /{store.slug}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onClickOpenStoreUpsertModal(store)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-blue-600 transition hover:bg-blue-50 dark:text-sky-400 dark:hover:bg-sky-400/10"
                    title="Edit store"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </button>
                </div>

                {/* Body */}
                <div className="space-y-5 px-5 py-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {/* Contact */}
                    <div>
                      <h4 className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                        Contact
                      </h4>
                      <ul className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                        {store.responsiblename && (
                          <li className="flex items-start gap-2">
                            <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                            <span>{store.responsiblename}</span>
                          </li>
                        )}
                        {store.phone && (
                          <li className="flex items-start gap-2">
                            <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                            <span>{store.phone}</span>
                          </li>
                        )}
                        <li className="flex items-start gap-2">
                          <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                          <div className="min-w-0 text-slate-700 dark:text-slate-200">
                            {renderAddress(store)}
                          </div>
                        </li>
                      </ul>
                    </div>

                    {/* Settings */}
                    <div>
                      <h4 className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                        Settings
                      </h4>
                      <ul className="mt-2 space-y-2 text-sm">
                        <li className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <ReceiptPercentIcon className="h-4 w-4" />
                            Tax Rate
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {store.tax ?? 0}%
                          </span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400">
                            Selected Tip
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {store.selectedtip ?? '—'}
                          </span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400">
                            Customer Fee
                          </span>
                          {store.customerfeeactive ? (
                            <span className="inline-flex h-6 items-center rounded-md bg-emerald-50 px-2 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                              {store.customerfee}
                              {store.customerfeeisrate ? '%' : '$'}
                            </span>
                          ) : (
                            <span className="inline-flex h-6 items-center rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-500 dark:bg-slate-700/60 dark:text-slate-300">
                              Disabled
                            </span>
                          )}
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Store URL */}
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
                    <div className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                      <LinkIcon className="h-3.5 w-3.5" />
                      Store URL
                    </div>
                    <a
                      href={orderUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block truncate text-sm font-medium text-blue-600 hover:underline dark:text-sky-400"
                    >
                      {orderUrl}
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <StoreUpsertModal
        show={dialogs.upsert}
        onCloseUpsert={() => setDialogs({ ...dialogs, upsert: false })}
        storeDetail={selectedStore}
        checkUpdate={checkUpdate}
      />
    </section>
  );
};

export default Stores;
